import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { clsx } from 'clsx'
import { Button, Input } from '../../components/ui'
import { useModal } from '../../context/ModalContext'
import { authApi } from '../../lib/api/auth'
import { ApiRequestError } from '../../lib/api/client'

const schema = z
  .object({
    old_password: z.string().min(1, 'Requerido'),
    new_password: z
      .string()
      .min(8, 'Mínimo 8 caracteres')
      .regex(/[a-z]/, 'Falta una minúscula')
      .regex(/[A-Z]/, 'Falta una mayúscula')
      .regex(/[0-9]/, 'Falta un número'),
  })
  .refine((v) => v.old_password !== v.new_password, {
    message: 'Debe ser distinta a la actual',
    path: ['new_password'],
  })
type FormValues = z.infer<typeof schema>

const RULES = [
  { test: (v: string) => v.length >= 8, label: 'Al menos 8 caracteres' },
  { test: (v: string) => /[a-z]/.test(v), label: 'Una minúscula' },
  { test: (v: string) => /[A-Z]/.test(v), label: 'Una mayúscula' },
  { test: (v: string) => /[0-9]/.test(v), label: 'Un número' },
]

export function PasswordModalContent({ onDone }: { onDone: () => void }) {
  const { alert } = useModal()
  const [submitting, setSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { old_password: '', new_password: '' } })

  const newPassword = watch('new_password') ?? ''

  async function onSubmit(values: FormValues) {
    setSubmitting(true)
    try {
      await authApi.changePassword(values)
      onDone()
      await alert('Contraseña', 'Contraseña cambiada')
    } catch (err) {
      await alert('No se pudo cambiar', err instanceof ApiRequestError ? err.message : 'Error inesperado')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      <Input
        type="password"
        label="Contraseña actual"
        {...register('old_password')}
        error={errors.old_password?.message}
      />
      <Input
        type="password"
        label="Nueva contraseña"
        {...register('new_password')}
        error={errors.new_password?.message}
      />
      <ul className="flex flex-col gap-1 text-xs">
        {RULES.map((rule) => {
          const ok = rule.test(newPassword)
          return (
            <li key={rule.label} className={clsx('flex items-center gap-1.5', ok ? 'text-green' : 'text-muted')}>
              <span aria-hidden>{ok ? '✓' : '○'}</span>
              {rule.label}
            </li>
          )
        })}
      </ul>
      <div className="mt-1 flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </form>
  )
}
